import { env } from "./env.ts";

// Cast API client. Sole boundary for talking to kuchikomi-night-api's
// /api/v1/cast/* endpoints. Owns the in-memory access token; a page reload
// wipes it, forcing the caller back through LIFF verify.

let accessToken: string | null = null;

export function getAccessToken(): string | null {
	return accessToken;
}

export function setAccessToken(token: string | null): void {
	accessToken = token;
}

export class ApiError extends Error {
	readonly status: number;
	readonly code: string | undefined;

	constructor(status: number, message: string, code?: string) {
		super(message);
		this.status = status;
		this.code = code;
	}
}

interface ErrorBody {
	code?: string;
	message?: string;
}

async function readErrorBody(res: Response): Promise<ErrorBody> {
	try {
		return (await res.json()) as ErrorBody;
	} catch {
		return {};
	}
}

interface RequestOptions {
	body?: unknown;
	skipAuth?: boolean;
	method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
	// Internal: skip the 401→refresh→retry loop. Set by the refresh call itself
	// so a failing /refresh doesn't recurse.
	skipRefresh?: boolean;
}

// De-duplicates concurrent refresh calls. When several requests race a 401 at
// once, only the first one hits /cast/auth/refresh; the rest await the same
// promise, then retry with the new access token. Cleared when the refresh
// settles (success or failure) so the next 401 wave can trigger a fresh one.
let refreshInFlight: Promise<boolean> | null = null;

async function refreshAccessToken(): Promise<boolean> {
	if (refreshInFlight) {
		return refreshInFlight;
	}
	refreshInFlight = (async () => {
		try {
			const res = await fetch(`${env.apiBaseUrl}/api/v1/cast/auth/refresh`, {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({}),
				credentials: "include",
			});
			if (!res.ok) return false;
			const json = (await res.json()) as { data?: { accessToken?: string } };
			const next = json.data?.accessToken;
			if (typeof next !== "string" || next.length === 0) return false;
			accessToken = next;
			return true;
		} catch {
			return false;
		} finally {
			// Clear on next microtask so awaiters of the current in-flight promise
			// still see the settled value.
			queueMicrotask(() => {
				refreshInFlight = null;
			});
		}
	})();
	return refreshInFlight;
}

async function rawFetch(path: string, opts: RequestOptions): Promise<Response> {
	const headers = new Headers();
	if (opts.body !== undefined) {
		headers.set("Content-Type", "application/json");
	}
	if (!opts.skipAuth && accessToken) {
		headers.set("Authorization", `Bearer ${accessToken}`);
	}
	return fetch(`${env.apiBaseUrl}${path}`, {
		method: opts.method ?? (opts.body !== undefined ? "POST" : "GET"),
		headers,
		body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
		// `cast_rt` refresh cookie needs credentialed CORS to travel between the
		// cast host and api.<env>.kuchikominight.com. Local dev works because
		// Vite runs on a distinct port and the API's CORS_ORIGINS allowlist
		// includes localhost:3200.
		credentials: "include",
	});
}

async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
	let res = await rawFetch(path, opts);

	// 401 on an authed request → try one refresh, then retry once. skipRefresh
	// avoids recursion when /cast/auth/refresh itself 401s. skipAuth requests
	// (verify / claim-invitation) don't refresh — a 401 there means the caller
	// (LIFF ID token or invitation token) is genuinely invalid.
	if (res.status === 401 && !opts.skipAuth && !opts.skipRefresh) {
		const refreshed = await refreshAccessToken();
		if (refreshed) {
			res = await rawFetch(path, opts);
		}
	}

	if (!res.ok) {
		const err = await readErrorBody(res);
		throw new ApiError(res.status, err.message ?? res.statusText, err.code);
	}

	if (res.status === 204) {
		return undefined as T;
	}
	return (await res.json()) as T;
}

// ----- Cast auth -----

export interface CastMe {
	id: string;
	lineUserId: string | null;
	name: string;
	status: string;
}

export interface CastAuthTokens {
	cast: CastMe;
	accessToken: string;
	refreshToken: string;
}

interface DataEnvelope<T> {
	data: T;
}

export async function lineVerify(lineIdToken: string): Promise<CastMe> {
	const { data } = await request<DataEnvelope<CastAuthTokens>>("/api/v1/cast/auth/line/verify", {
		body: { lineIdToken },
		skipAuth: true,
	});
	setAccessToken(data.accessToken);
	return data.cast;
}

export async function claimInvitation(
	invitationToken: string,
	lineIdToken: string,
): Promise<CastMe> {
	const { data } = await request<DataEnvelope<CastAuthTokens>>(
		"/api/v1/cast/auth/claim-invitation",
		{
			body: { token: invitationToken, lineIdToken },
			skipAuth: true,
		},
	);
	setAccessToken(data.accessToken);
	return data.cast;
}

export async function fetchMe(): Promise<CastMe> {
	const { data } = await request<DataEnvelope<CastMe>>("/api/v1/cast/auth/me");
	return data;
}

// ----- Profile self-edit -----
// Backed by PATCH /api/v1/casts/me (kuchikomi-night-api#387). Strict schema on
// the API side — sending any field not listed here returns 422. The response
// is the full cast row; we surface the profile-only subset the form cares about.
export interface CastProfile {
	id: string;
	name: string;
	age: number | null;
	height: number | null;
	bust: number | null;
	waist: number | null;
	hip: number | null;
	cupSize: string | null;
	bloodType: string | null;
	selfIntroduction: string | null;
}

export type CastProfileInput = Partial<Omit<CastProfile, "id">>;

// GET the full cast row (public read via /casts/{id}?locale=ja). Used by the
// profile page to pre-populate the form. fetchMe returns only id/lineUserId/
// name/status, which isn't enough to render the edit form.
export async function fetchOwnProfile(castId: string): Promise<CastProfile> {
	const { data } = await request<DataEnvelope<CastProfile>>(`/api/v1/casts/${castId}?locale=ja`);
	return data;
}

export async function updateOwnProfile(input: CastProfileInput): Promise<CastProfile> {
	const { data } = await request<DataEnvelope<CastProfile>>("/api/v1/casts/me", {
		method: "PATCH",
		body: input,
	});
	return data;
}

// ----- Attendance (shift) -----
// Backed by POST /api/v1/casts/me/attendance (kuchikomi-night-api#377). The
// endpoint is bulk-shaped so a future UI can batch multiple dates in one
// request; the current form submits one entry at a time. `castId` is derived
// server-side from the authenticated cast subject, so the request body has no
// castId field — a cast cannot submit for another cast.
export interface AttendanceEntry {
	id: string;
	castId: string;
	shopId: string;
	date: string;
	startTime: string;
	endTime: string;
	isConfirmed: boolean;
	note: string | null;
}

export interface AttendanceEntryInput {
	date: string;
	startTime: string;
	endTime: string;
	note?: string | null;
}

export async function submitAttendance(
	entries: AttendanceEntryInput[],
): Promise<AttendanceEntry[]> {
	const { data } = await request<DataEnvelope<AttendanceEntry[]>>("/api/v1/casts/me/attendance", {
		body: { entries },
	});
	return data;
}

// GET /api/v1/casts/{castId}/schedule — upcoming (today onward). The endpoint
// is public; the cast is authenticated so the caller pattern matches the rest
// of the app, but no audience-guarded write is happening here.
export async function listMyShifts(castId: string): Promise<AttendanceEntry[]> {
	const { data } = await request<DataEnvelope<AttendanceEntry[]>>(
		`/api/v1/casts/${castId}/schedule`,
	);
	return data;
}

// PUT /api/v1/attendances/{id} — cast edits their OWN row.
// kuchikomi-night-api#385 widened the audience-guard on attendance-admin.route
// to include "cast", and the service enforces `attendance.castId === subject.id`.
export async function updateAttendance(
	attendanceId: string,
	input: { startTime?: string; endTime?: string; note?: string | null },
): Promise<AttendanceEntry> {
	const { data } = await request<DataEnvelope<AttendanceEntry>>(
		`/api/v1/attendances/${attendanceId}`,
		{ body: input, method: "PUT" },
	);
	return data;
}

// DELETE /api/v1/attendances/{id} — cast deletes their OWN row.
export async function deleteAttendance(attendanceId: string): Promise<void> {
	await request<DataEnvelope<AttendanceEntry> | undefined>(`/api/v1/attendances/${attendanceId}`, {
		method: "DELETE",
	});
}

// ----- Diary (写メ日記) -----
// Backed by POST /api/v1/casts/{castId}/diary (createDiaryRequestSchema:
// title, content, publishState default "draft", publishAt, images). The
// audience-guard on the API side allows cast in addition to shop_staff /
// system_admin, and the service layer's ensureOwnership check accepts the
// call when subject.aud === "cast" and subject.id === castId — so a cast
// can only post to their own diary.
export interface DiaryEntry {
	id: string;
	castId: string;
	title: string;
	content: string;
	publishState: "draft" | "scheduled" | "published";
	publishAt: string | null;
	createdAt: string;
	updatedAt: string;
}

export async function postDiary(
	castId: string,
	input: {
		title: string;
		content: string;
		publishState?: "draft" | "scheduled";
		publishAt?: string | null;
	},
): Promise<DiaryEntry> {
	const { data } = await request<DataEnvelope<DiaryEntry>>(`/api/v1/casts/${castId}/diary`, {
		body: input,
	});
	return data;
}

interface PaginatedDiary {
	data: DiaryEntry[];
	pagination?: { nextCursor: string | null; hasMore: boolean };
}

// GET /api/v1/casts/{castId}/diary/manage — cast's own list including drafts
// and scheduled. audienceGuard on the endpoint already accepts cast.
export async function listMyDiaries(castId: string): Promise<DiaryEntry[]> {
	const res = await request<PaginatedDiary>(`/api/v1/casts/${castId}/diary/manage`);
	return res.data;
}

// PATCH /api/v1/diary/{id} — cast edits their own diary. The API's
// updateDiaryRequestSchema is `.strict()` and forbids publishState here; the
// dedicated state-transition endpoint is out of scope for MVP.
export async function updateDiary(
	diaryId: string,
	input: { title?: string; content?: string; publishAt?: string | null },
): Promise<DiaryEntry> {
	const { data } = await request<DataEnvelope<DiaryEntry>>(`/api/v1/diary/${diaryId}`, {
		body: input,
		method: "PATCH",
	});
	return data;
}

// DELETE /api/v1/diary/{id} — cast deletes their own diary.
export async function deleteDiary(diaryId: string): Promise<void> {
	await request<DataEnvelope<DiaryEntry> | undefined>(`/api/v1/diary/${diaryId}`, {
		method: "DELETE",
	});
}
